import { Mic, Search, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input, type InputProps } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useSpeechToText } from "@/hooks/useSpeechRecognition";
import { ListeningIcon } from "@/components/ListeningIcon";
import { forwardRef, useEffect, useState } from "react";
import useLanguageContext from "@/hooks/useLanguageContext";

export interface SearchInputProps extends InputProps {
  onSearch: (searchTerm: string) => void;
  isLoading?: boolean;
}

const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ className, onSearch, isLoading = false, ...props }, ref) => {
    const { apiLang } = useLanguageContext();
    const [searchTerm, setSearchTerm] = useState("");
    const [speechLang, setSpeechLang] = useState("mr-IN");

    useEffect(() => {
      if (apiLang === "0") {
        setSpeechLang("en-US");
      } else {
        setSpeechLang("mr-IN");
      }
    }, [apiLang]);

    const {
      transcript,
      isListening,
      startListening,
      stopListening,
      browserSupportsVoice,
    } = useSpeechToText({
      id: "global-search",
      onTranscriptEnd: onSearch,
      lang: speechLang,
    });

    useEffect(() => {
      if (transcript) {
        setSearchTerm(transcript);
      }
    }, [transcript]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
    };

    const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      onSearch(searchTerm);
    };

    const handleVoiceSearch = () => {
      if (isListening) {
        stopListening();
      } else {
        startListening();
      }
    };

    return (
      <form
        onSubmit={handleSearch}
        className={cn("w-full max-w-lg", className)}
        role="search"
      >
        <div className="relative  w-full items-center">
          <Input
            type="search"
            name="search"
            placeholder={isListening ? "Listening..." : "Search..."}
            value={searchTerm}
            onChange={handleInputChange}
            className="h-10 w-full rounded-full border border-input bg-background text-sm pr-20 ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            ref={ref}
            disabled={isLoading || isListening}
            {...props}
            id="site-search"
            aria-label="Search the site"
          />

          <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center">
            {browserSupportsVoice && (
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className={cn(
                  "h-8 w-8 rounded-full",
                  isListening && "text-red-500 animate-pulse bg-red-500/10"
                )}
                onClick={handleVoiceSearch}
                disabled={isLoading}
                aria-label={isListening ? "Stop listening" : "Search by voice"}
              >
                {isListening ? <ListeningIcon /> : <Mic className="h-4 w-4" />}
              </Button>
            )}

            <Button
              type="submit"
              size="icon"
              className="h-8 w-8 rounded-full bg-theme-light-green"
              aria-label="Search"
              disabled={isLoading || isListening}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" role="presentation" />
              ) : (
                <Search className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </form>
    );
  }
);

SearchInput.displayName = "SearchInput";

export default SearchInput;
